import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { BookOpen, Lock, CheckCircle2 } from 'lucide-react';

const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError('Passwort muss mindestens 8 Zeichen haben.'); return; }
    if (password !== confirm) { setError('Passwörter stimmen nicht überein.'); return; }
    setBusy(true); setError(null);
    try {
      await api('/api/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: any) {
      setError(err?.message || 'Fehler');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-6">
          <BookOpen className="w-10 h-10 text-blue-500" />
          <h1 className="text-3xl font-bold text-white">Hausaufgabenheft</h1>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
            <Lock className="w-5 h-5" /> Neues Passwort
          </h2>

          {!token ? (
            <div className="mt-4 bg-red-500/10 border border-red-500/40 text-red-300 rounded-lg p-4 text-sm">
              Kein Token vorhanden. Fordere unter <Link to="/forgot-password" className="underline">Passwort vergessen</Link> einen neuen Link an.
            </div>
          ) : done ? (
            <div className="mt-4 space-y-3">
              <div className="bg-green-500/10 border border-green-500/40 text-green-300 rounded-lg p-4 text-sm flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                <div>
                  Passwort geändert. Du wirst zum Login weitergeleitet…
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4 mt-4">
              <p className="text-sm text-gray-400">Wähle ein neues Passwort (min. 8 Zeichen).</p>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Neues Passwort</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8}
                  autoFocus
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Passwort bestätigen</label>
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500" />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button type="submit" disabled={busy}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg">
                {busy ? 'Speichert…' : 'Passwort festlegen'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
